import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Edit, X, Search } from 'lucide-react';
import Avatar from './Avatar';
import { getAnchoredOverlayStyle } from '../../utils/viewportPosition';

const UserSelect = ({
  currentUser,
  currentUserId,
  userOptions = [],
  isEditing = false,
  isLoading = false,
  onEdit,
  onCancel,
  onChange,
  emptyLabel = "No linked user",
  selectPlaceholder = "Search user..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filteredUsers = userOptions.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const name = user.name?.toLowerCase() || '';
    const email = user.email?.toLowerCase() || '';
    return name.includes(searchLower) || email.includes(searchLower);
  });

  useEffect(() => {
    if (isEditing) {
      setSearchTerm('');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isEditing]);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current || !isEditing) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle(getAnchoredOverlayStyle({
      anchorRect: rect,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      preferredWidth: Math.max(250, rect.width),
    }));
  }, [isEditing]);

  useEffect(() => {
    if (isEditing) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isEditing, updateDropdownPosition]);

  const handleSelectUser = (userId) => {
    onChange(userId);
    setSearchTerm('');
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        {currentUser || currentUserId ? (
          <>
            <Avatar
              avatar={currentUser?.avatar}
              name={currentUser?.name}
              email={currentUser?.email}
              size="sm"
              showInitialOnMissing={false}
            />
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {currentUser?.name || currentUser?.email || `ID: ${currentUserId}`}
              </span>
              {currentUser?.name && currentUser?.email && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {currentUser.email}
                </span>
              )}
            </div>
          </>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">
            {emptyLabel}
          </span>
        )}
        {onEdit && (
          <button
            onClick={onEdit}
            className="p-1 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors flex-shrink-0"
            title="Edit linked user"
          >
            <Edit className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full min-w-0 sm:w-[250px]">
      <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={selectPlaceholder}
        disabled={isLoading}
        className="block w-full pl-7 pr-7 py-1.5 text-xs border border-light-border-primary dark:border-dark-border-primary rounded-md bg-light-bg-primary dark:bg-dark-bg-secondary text-light-text-primary dark:text-dark-text-primary placeholder-light-text-tertiary dark:placeholder-dark-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {onCancel && (
        <button
          onClick={onCancel}
          disabled={isLoading}
          title="Cancel"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {createPortal(
        <div
          style={dropdownStyle}
          className="overflow-auto border border-light-border-primary dark:border-dark-border-primary rounded-md bg-light-bg-primary dark:bg-dark-bg-secondary shadow-lg"
        >
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => {
              const isSelected = currentUserId === user.id || currentUser?.id === user.id;
              return (
                <button
                  key={user.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectUser(user.id)}
                  disabled={isLoading}
                  className={`w-full px-2 py-1.5 text-left text-xs flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-tertiary'
                  }`}
                >
                  <Avatar
                    avatar={user?.avatar}
                    name={user?.name}
                    email={user?.email}
                    size="xs"
                    showInitialOnMissing={false}
                  />
                  <span className="truncate">
                    {user.name || user.email}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="px-2 py-1.5 text-xs text-light-text-tertiary dark:text-dark-text-tertiary italic">
              {isLoading ? 'Loading...' : 'No users found'}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserSelect;
